// ProductSizes.jsx  — full page (keeps ALL modals)
// Shows per-location counts from StockUnits so transfers/backfills stay in sync.

import { useEffect, useRef, useState } from "react";
import CreateButton from "../../../../Partials/Layouts/CRUDs/CRUDHeader/CreateButton/CreateButton";
import CRUDTable from "../../../../Partials/Layouts/CRUDs/CRUDTable/CRUDTable";
import CRUDTableHeader from "../../../../Partials/Layouts/CRUDs/CRUDTable/CRUDTableHeader/CRUDTableHeader";
import CRUDth from "../../../../Partials/Layouts/CRUDs/CRUDTable/CRUDTableHeader/CRUDth/CRUDth";
import ViewButton from "../../../../Partials/Layouts/CRUDs/CRUDButtons/ViewButton/ViewButton";
import EditButton from "../../../../Partials/Layouts/CRUDs/CRUDButtons/EditButton/EditButton";
import DeleteButton from "../../../../Partials/Layouts/CRUDs/CRUDButtons/DeleteButton/DeleteButton";
import ShortTextCell from "../../../../Partials/Layouts/CRUDs/CRUDTable/CRUDTableCells/ShortTextCell/ShortTextCell";
import CRUDTableRow from "../../../../Partials/Layouts/CRUDs/CRUDTable/CRUDTableRow/CRUDTableRow";
import Modal from "../../../../Partials/Elements/Modal/Modal";
import useAxiosInstance from "../../../../../Utilities/Hooks/useAxiosInstance";
import DeleteItem from "../../../../Partials/Layouts/DeleteItem/DeleteItem";
import { PRODUCTSIZE_API } from "../../../../../Utilities/APIs/APIs";
import ViewProductSize from "./ViewProductSize";
import CreateProductSize from "./CreateProductSize";
import UpdateProductSize from "./UpdateProductSize";
import CRUDButton from "../../../../Partials/Layouts/CRUDs/CRUDButtons/CRUDButton";
import { RiImage2Line } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";
import TransferStockModal from "./TransferStockModal";
import BackfillBarcodesModal from "./BackfillBarcodesModal";

function ProductSizes() {
  const [productsizes, setProductSizes] = useState([]);
  const [countsMap, setCountsMap] = useState({}); // { [sizeId]: { total, byLocation:{mainOffice,showRoom,wareHouse} } }
  const [loadingCounts, setLoadingCounts] = useState(false);

  const screenHeaerRef = useRef();
  const [screenHeaderHeight, setScreenHeaderHeight] = useState(0);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showViewSection, setShowViewSection] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [showDeleteSection, setShowDeleteSection] = useState(false);

  const [showTransfer, setShowTransfer] = useState(false);
  const [showBackfill, setShowBackfill] = useState(false);

  const [targetID, setTargetID] = useState(null);
  const [currentPS, setCurrentPS] = useState(null);

  const [toggleFetch, setToggleFetch] = useState(false);
  const [staffName, setStaffName] = useState("");

  const axios = useAxiosInstance();
  const navigate = useNavigate();
  const { productID } = useParams();

  useEffect(() => {
    const readStaffFromStorage = () => {
      try {
        const keysToTry = [
          "employeeInfo",
          "employee",
          "employeeAuth",
          "userInfo",
          "auth",
          "token", // sometimes stores string only (won't help)
        ];

        for (const k of keysToTry) {
          const raw = localStorage.getItem(k);
          if (!raw) continue;

          // If it's JSON
          if (raw.trim().startsWith("{")) {
            const obj = JSON.parse(raw);

            // common shapes
            const candidate =
              obj?.name ||
              obj?.employee?.name ||
              obj?.user?.name ||
              obj?.data?.name ||
              "";

            if (candidate) return candidate.toString();
          }
        }
      } catch (e) {
        // ignore
      }
      return "";
    };

    const nameFromLS = readStaffFromStorage();
    if (nameFromLS) {
      setStaffName(nameFromLS);
      return;
    }

    // Fallback: if you have token in axiosInstance and an endpoint later, you can add it here
    // But we won't guess more endpoints to avoid breaking.
  }, []);

  useEffect(() => {
    const h = screenHeaerRef.current?.clientHeight || 0;
    setScreenHeaderHeight(h);
  }, [screenHeaerRef]);

  // 1) Load sizes
  useEffect(() => {
    async function loadSizes() {
      const { data } = await axios.get(
        PRODUCTSIZE_API + "getProductSizesByProduct/" + productID
      );
      setProductSizes(Array.isArray(data) ? data : []);
    }
    loadSizes();
  }, [axios, productID, toggleFetch]);

  // 2) Load per-location counts from StockUnits for each size (authoritative)
  useEffect(() => {
    if (!productsizes.length) {
      setCountsMap({});
      return;
    }
    let cancel = false;

    async function loadCounts() {
      setLoadingCounts(true);
      try {
        const pairs = await Promise.all(
          productsizes.map(async (ps) => {
            try {
              const { data } = await axios.get(
                `/inventory/stockUnits/counts/by-size/${ps._id}`
              );
              return [ps._id, data || { total: 0, byLocation: {} }];
            } catch {
              return [ps._id, { total: 0, byLocation: {} }];
            }
          })
        );
        if (!cancel) {
          const map = {};
          for (const [id, val] of pairs) map[id] = val;
          setCountsMap(map);
        }
      } finally {
        if (!cancel) setLoadingCounts(false);
      }
    }

    loadCounts();
    return () => {
      cancel = true;
    };
  }, [productsizes, axios]);

  function triggerFetch() {
    setToggleFetch((p) => !p);
  }

  // When a transfer or backfill succeeds, refresh both sizes (to pick up total) and counts
  async function handleInventoryActionSuccess() {
    triggerFetch(); // reload sizes; counts effect will refetch
  }

  return (
    <>
      <div className="screen_header" ref={screenHeaerRef}>
        <h1 className="screen_heading">
          Product Sizes ({productsizes?.length ?? 0} in total)
        </h1>
        <CreateButton
          screenTopicSingular={"Product Sizes"}
          setShowCreateForm={setShowCreateForm}
        />
      </div>

      <CRUDTable screenHeaderHeight={screenHeaderHeight}>
        <CRUDTableHeader>
          <CRUDth th="Product" />
          <CRUDth th="Size" />
          <CRUDth th="Main Office" />
          <CRUDth th="Showroom" />
          <CRUDth th="Warehouse" />
          <CRUDth th="Total" />
          <CRUDth th="Action" />
        </CRUDTableHeader>

        <tbody>
          {productsizes.map((ps) => {
            const c = countsMap[ps._id] || { total: 0, byLocation: {} };
            const mo = c.byLocation?.mainOffice || 0;
            const sr = c.byLocation?.showRoom || 0;
            const wh = c.byLocation?.wareHouse || 0;

            return (
              <CRUDTableRow key={ps._id}>
                <ShortTextCell text={ps?.product?.name} />
                <ShortTextCell text={ps?.size?.name} />

                <ShortTextCell text={loadingCounts ? "…" : mo} />
                <ShortTextCell text={loadingCounts ? "…" : sr} />
                <ShortTextCell text={loadingCounts ? "…" : wh} />

                {/* keep your backend's total field for now */}
                <ShortTextCell text={ps?.quantity ?? 0} />

                <td>
                  <div className="action_buttons_wrapper">
                    <div className="action_buttons">
                      <ViewButton
                        setShowViewSection={setShowViewSection}
                        targetID={ps._id}
                        setTargetID={setTargetID}
                      />

                      <EditButton
                        setShowUpdateForm={setShowUpdateForm}
                        targetID={ps._id}
                        setTargetID={setTargetID}
                      />

                      <DeleteButton
                        setShowDeleteSection={setShowDeleteSection}
                        targetID={ps._id}
                        setTargetID={setTargetID}
                      />

                      {staffName=== "Super Admin" && (
                        <CRUDButton
                          handleClick={() =>
                            navigate("/main/ChangedInventories/" + ps._id)
                          }
                          title="Inventory History"
                        >
                          <RiImage2Line />
                        </CRUDButton>
                      )}

                      <CRUDButton
                        title="Transfer"
                        handleClick={() => {
                          setCurrentPS(ps._id);
                          setShowTransfer(true);
                        }}
                      >
                        ⇄
                      </CRUDButton>

                      <CRUDButton
                        title="Backfill Barcodes"
                        handleClick={() => {
                          setCurrentPS(ps._id);
                          setShowBackfill(true);
                        }}
                      >
                        🧾
                      </CRUDButton>
                    </div>
                  </div>
                </td>
              </CRUDTableRow>
            );
          })}
        </tbody>
      </CRUDTable>

      {/* Create */}
      {showCreateForm && (
        <Modal
          extraClass={"small"}
          modalHeading={`Add Product Size`}
          setShowModalContent={setShowCreateForm}
        >
          <CreateProductSize
            productID={productID}
            triggerFetch={triggerFetch}
            setShowCreateForm={setShowCreateForm}
          />
        </Modal>
      )}

      {/* Update */}
      {showUpdateForm && (
        <Modal
          extraClass={"small"}
          modalHeading={`Update Product Size`}
          setShowModalContent={setShowUpdateForm}
        >
          <UpdateProductSize
            productID={productID}
            triggerFetch={triggerFetch}
            targetID={targetID}
            setShowUpdateForm={setShowUpdateForm}
          />
        </Modal>
      )}

      {/* View */}
      {showViewSection && (
        <Modal
          extraClass={"small"}
          modalHeading={`View Product Size`}
          setShowModalContent={setShowViewSection}
        >
          <ViewProductSize targetID={targetID} />
        </Modal>
      )}

      {/* Delete */}
      {showDeleteSection && (
        <Modal
          extraClass={"small"}
          modalHeading={`Delete Product Size`}
          setShowModalContent={setShowDeleteSection}
        >
          <DeleteItem
            api={PRODUCTSIZE_API}
            triggerFetch={triggerFetch}
            targetID={targetID}
            setShowDeleteItem={setShowDeleteSection}
          />
        </Modal>
      )}

      {/* Transfer */}
      {showTransfer && (
        <Modal
          extraClass={"small"}
          modalHeading={`Transfer Stock`}
          setShowModalContent={setShowTransfer}
        >
          <TransferStockModal
            productSizeId={currentPS}
            onClose={() => setShowTransfer(false)}
            onSuccess={handleInventoryActionSuccess}
          />
        </Modal>
      )}

      {/* Backfill Barcodes */}
      {showBackfill && (
        <Modal
          extraClass={"small"}
          modalHeading={`Backfill Barcodes`}
          setShowModalContent={setShowBackfill}
        >
          <BackfillBarcodesModal
            productSizeId={currentPS}
            onClose={() => setShowBackfill(false)}
            onSuccess={handleInventoryActionSuccess}
          />
        </Modal>
      )}
    </>
  );
}

export default ProductSizes;
